import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ShieldAlert, Ban, Phone, Mail, FileText, Calendar } from "lucide-react";
import { fmtDateTime12 } from "@/lib/time";

export default function AdminBlacklist() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blacklist");
      setRows(r.data);
    } catch {
      toast.error("Failed to load blacklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Remove from blacklist?")) return;
    await api.delete(`/admin/blacklist/${id}`);
    toast.success("Removed from blacklist");
    load();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-red-400 font-bold uppercase tracking-widest animate-pulse">Loading Blacklist Registry...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blacklist" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b-2 border-white/10 pb-6 space-y-2"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-red-400 font-extrabold flex items-center gap-2">
          <ShieldAlert size={16} /> MASTER ONLY · IMMUTABLE REGISTRY
        </div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-rose-300 to-amber-400 font-bold">
          Blacklist Registry
        </h1>
        <p className="text-sm text-white/70 max-w-3xl leading-relaxed">
          Therapists on this list cannot be added to any branch. Blacklist entries are enforced globally by ID, phone, and government ID.
        </p>
      </motion.div>

      {/* Cards Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {rows.map((r) => (
          <div
            key={r.id}
            className="rounded-2xl border border-red-500/70 p-6 bg-gradient-to-b from-red-950/30 via-black/90 to-red-950/20 shadow-xl shadow-red-950/20 flex flex-col justify-between space-y-4 hover:scale-[1.01] transition-all"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-3 border-b border-red-500/20">
                <div className="flex items-center gap-2">
                  <Ban className="text-red-400 shrink-0" size={18} />
                  <h3 className="font-heading text-2xl font-bold text-white">{r.display_name}</h3>
                </div>
                <button
                  onClick={() => remove(r.id)}
                  title="Remove from Blacklist"
                  className="p-2 rounded-xl border border-red-500/40 bg-red-950/30 hover:bg-red-900/60 text-red-300 transition-all"
                >
                  <Trash2 size={15} />
                </button>
              </div>

              <div className="space-y-1.5 text-xs text-white/70">
                <div className="flex items-center gap-2">
                  <Phone size={13} className="text-red-400 shrink-0" />
                  <span>Phone: <strong className="text-white">{r.phone || "—"}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText size={13} className="text-red-400 shrink-0" />
                  <span>Gov ID: <strong className="text-white">{r.gov_id || "—"}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={13} className="text-red-400 shrink-0" />
                  <span className="truncate">Email: <strong className="text-white">{r.email || "—"}</strong></span>
                </div>
              </div>

              <div className="p-4 rounded-xl border border-red-500/30 bg-black/60 space-y-1">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-red-400">Blacklist Reason</div>
                <div className="text-sm font-semibold text-red-200">{r.reason}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[11px] text-white/40 pt-2 border-t border-white/10 font-semibold">
              <Calendar size={12} className="text-red-400" />
              <span>Blacklisted on {fmtDateTime12(r.created_at)}</span>
            </div>
          </div>
        ))}

        {rows.length === 0 && (
          <div className="col-span-full text-center py-16 text-white/50 bg-hpaper border border-white/10 rounded-2xl font-medium">
            No blacklist entries currently registered.
          </div>
        )}
      </motion.div>
    </div>
  );
}
